import { isOfflinePreset, type ProviderPreset } from './providers';

export interface AiDeviceConfig {
  version: 1;
  preset: ProviderPreset;
  baseUrl: string;
  model: string;
  apiKey: string;
  apiKeyTarget: string;
}

export interface SealedDeviceConfig {
  version: 1;
  iv: number[];
  ciphertext: ArrayBuffer;
}

const DATABASE_NAME = 'yijing-atlas-private-settings';
const DATABASE_VERSION = 1;
const STORE_NAME = 'encrypted-ai-config';
const CONFIG_RECORD = 'config';
const KEY_RECORD = 'device-key';
const VALID_PRESETS = new Set<ProviderPreset>([
  'auto', 'rules', 'browser', 'browserQuality', 'deepseek', 'openai', 'local', 'custom'
]);
let mutationQueue: Promise<void> = Promise.resolve();

function enqueueMutation(task: () => Promise<void>): Promise<void> {
  const queued = mutationQueue.then(task, task);
  mutationQueue = queued.catch(() => undefined);
  return queued;
}

function webCrypto(): SubtleCrypto {
  if (!globalThis.crypto?.subtle) {
    throw new Error('当前浏览器不支持加密保存，请每次使用时重新填写 API Key。');
  }
  return globalThis.crypto.subtle;
}

function encode(value: string): Uint8Array<ArrayBuffer> {
  return new TextEncoder().encode(value) as Uint8Array<ArrayBuffer>;
}

function decode(value: ArrayBuffer): string {
  return new TextDecoder().decode(value);
}

export async function sealDeviceConfig(config: AiDeviceConfig, key: CryptoKey): Promise<SealedDeviceConfig> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const ciphertext = await webCrypto().encrypt({ name: 'AES-GCM', iv }, key, encode(JSON.stringify(config)));
  return { version: 1, iv: Array.from(iv), ciphertext };
}

export async function openDeviceConfig(sealed: SealedDeviceConfig, key: CryptoKey): Promise<AiDeviceConfig> {
  if (
    sealed.version !== 1
    || !Array.isArray(sealed.iv)
    || sealed.iv.length !== 12
    || !sealed.ciphertext
    || typeof sealed.ciphertext.byteLength !== 'number'
  ) {
    throw new Error('本机保存的 AI 设置格式无效。');
  }
  const plaintext = await webCrypto().decrypt(
    { name: 'AES-GCM', iv: new Uint8Array(sealed.iv) },
    key,
    sealed.ciphertext
  );
  return validateConfig(JSON.parse(decode(plaintext)) as unknown);
}

function validateConfig(value: unknown): AiDeviceConfig {
  if (!value || typeof value !== 'object') throw new Error('本机保存的 AI 设置格式无效。');
  const candidate = value as Partial<AiDeviceConfig>;
  if (
    candidate.version !== 1
    || typeof candidate.preset !== 'string'
    || !VALID_PRESETS.has(candidate.preset as ProviderPreset)
    || typeof candidate.baseUrl !== 'string'
    || typeof candidate.model !== 'string'
    || typeof candidate.apiKey !== 'string'
    || typeof candidate.apiKeyTarget !== 'string'
    || candidate.baseUrl.length > 2_000
    || candidate.model.length > 300
    || candidate.apiKey.length > 8_000
    || candidate.apiKeyTarget.length > 2_000
  ) {
    throw new Error('本机保存的 AI 设置格式无效。');
  }
  return candidate as AiDeviceConfig;
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('读取本机 AI 设置失败。'));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(transaction.error ?? new Error('保存本机 AI 设置失败。'));
    transaction.onerror = () => reject(transaction.error ?? new Error('保存本机 AI 设置失败。'));
  });
}

function openDatabase(): Promise<IDBDatabase> {
  if (!globalThis.indexedDB) {
    return Promise.reject(new Error('当前浏览器不支持本机加密保存。'));
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error('打开本机 AI 设置失败。'));
    request.onblocked = () => reject(new Error('本机 AI 设置正在被另一个页面占用，请关闭旧页面后重试。'));
  });
}

async function readRecords(database: IDBDatabase): Promise<{ key?: CryptoKey; sealed?: SealedDeviceConfig }> {
  const transaction = database.transaction(STORE_NAME, 'readonly');
  const done = transactionDone(transaction);
  const store = transaction.objectStore(STORE_NAME);
  const [key, sealed] = await Promise.all([
    requestResult(store.get(KEY_RECORD)) as Promise<CryptoKey | undefined>,
    requestResult(store.get(CONFIG_RECORD)) as Promise<SealedDeviceConfig | undefined>
  ]);
  await done;
  return { key, sealed };
}

async function getOrCreateKey(database: IDBDatabase): Promise<CryptoKey> {
  const { key } = await readRecords(database);
  if (key) return key;
  const created = await webCrypto().generateKey({ name: 'AES-GCM', length: 256 }, false, ['encrypt', 'decrypt']);
  const transaction = database.transaction(STORE_NAME, 'readwrite');
  const done = transactionDone(transaction);
  transaction.objectStore(STORE_NAME).put(created, KEY_RECORD);
  await done;
  return created;
}

/**
 * 设置只进入当前浏览器的 IndexedDB；API Key 使用不可导出的 AES-GCM 密钥加密。
 * 这能避免磁盘中出现明文，但不能抵御已经在同一站点上下文执行的恶意脚本。
 */
async function saveAiDeviceConfigNow(config: AiDeviceConfig): Promise<void> {
  const validated = validateConfig(config);
  const database = await openDatabase();
  try {
    const key = await getOrCreateKey(database);
    const sealed = await sealDeviceConfig(validated, key);
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    transaction.objectStore(STORE_NAME).put(sealed, CONFIG_RECORD);
    await done;
  } finally {
    database.close();
  }
}

export function saveAiDeviceConfig(config: AiDeviceConfig): Promise<void> {
  return enqueueMutation(() => saveAiDeviceConfigNow(config));
}

export async function loadAiDeviceConfig(): Promise<AiDeviceConfig | null> {
  await mutationQueue;
  const database = await openDatabase();
  try {
    const { key, sealed } = await readRecords(database);
    if (!key || !sealed) return null;
    const config = await openDeviceConfig(sealed, key);
    if (isOfflinePreset(config.preset) && config.apiKey) {
      return { ...config, apiKey: '', apiKeyTarget: '' };
    }
    return config;
  } finally {
    database.close();
  }
}

async function clearAiDeviceConfigNow(): Promise<void> {
  const database = await openDatabase();
  try {
    const transaction = database.transaction(STORE_NAME, 'readwrite');
    const done = transactionDone(transaction);
    const store = transaction.objectStore(STORE_NAME);
    store.delete(CONFIG_RECORD);
    store.delete(KEY_RECORD);
    await done;
  } finally {
    database.close();
  }
}

export function clearAiDeviceConfig(): Promise<void> {
  return enqueueMutation(clearAiDeviceConfigNow);
}
